import React from 'react';
import SeoHelmet from '../components/SeoHelmet';
import { Link } from 'react-router-dom';
import HeroBanner from '../components/HeroBanner.tsx';
import Services from '../components/Services.tsx';
import ServicesSection from '../components/ServicesSection';
import Stats from '../components/Stats.tsx';
import Portfolio from '../components/Portfolio.tsx';
import ClientSlider from '../components/ClientSlider.tsx';
import BlogNews from '../components/BlogNews.tsx';
import BlogSlider from '../components/BlogSlider.tsx';
import ContactForm from '../components/ContactForm.tsx';

export default function Home() {
  // Schema markup for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Intense Infotech",
    "url": "https://www.intenseinfotech.com",
    "logo": "https://www.intenseinfotech.com/images/logo.jpeg",
    "description": "Leading web development company in India offering custom web design, React development, WordPress solutions, and comprehensive digital marketing services.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN",
      "addressRegion": "India"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-XXXXXXXXXX",
      "contactType": "customer service",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/intense-infotech",
      "https://twitter.com/intenseinfotech"
    ],
    "foundingDate": "2019",
    "numberOfEmployees": "50-100",
    "areaServed": "India",
    "serviceType": ["Web Development", "Digital Marketing", "SEO Services", "WordPress Development", "React Development"]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Intense Infotech",
    "url": "https://www.intenseinfotech.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.intenseinfotech.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Web Development Services in India",
    "description": "Professional web development, React applications, WordPress development, and digital marketing services",
    "provider": {
      "@type": "Organization",
      "name": "Intense Infotech"
    },
    "areaServed": "India",
    "serviceType": "Web Development",
    "offers": {
      "@type": "Offer",
      "description": "Custom web development solutions"
    }
  };

  return (
    <>
      <SeoHelmet>
        {/* Primary Meta Tags */}
        <title>Website Development Company in India | Web Design & SEO – Intense Infotech</title>
        <meta name="title" content="Website Development Company in India | Web Design & SEO – Intense Infotech" />
        <meta name="description" content="Leading web development company in India. Expert React development, WordPress solutions, SEO services & digital marketing. 150+ projects completed. Get free quote!" />
        <meta name="keywords" content="web development company india, react development, wordpress development, seo services india, digital marketing company, custom web applications, ecommerce development, web design services" />
        <meta name="author" content="Intense Infotech" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.intenseinfotech.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.intenseinfotech.com/" />
        <meta property="og:title" content="Website Development Company in India | Web Design & SEO – Intense Infotech" />
        <meta property="og:description" content="Leading web development company in India. Expert React development, WordPress solutions, SEO services & digital marketing. 150+ projects completed. Get free quote!" />
        <meta property="og:image" content="https://www.intenseinfotech.com/images/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Intense Infotech" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.intenseinfotech.com/" />
        <meta property="twitter:title" content="Website Development Company in India | Web Design & SEO – Intense Infotech" />
        <meta property="twitter:description" content="Leading web development company in India. Expert React development, WordPress solutions, SEO services & digital marketing. 150+ projects completed. Get free quote!" />
        <meta property="twitter:image" content="https://www.intenseinfotech.com/images/twitter-image.jpg" />
        <meta property="twitter:creator" content="@intenseinfotech" />
        <meta property="twitter:site" content="@intenseinfotech" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#2563eb" />
        <meta name="msapplication-TileColor" content="#2563eb" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        
        {/* Language and Geo Tags */}
        <meta name="language" content="English" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.country" content="India" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </SeoHelmet>

      {/* SEO-Optimized Content Structure */}
      <main>
        {/* Hero Section with H1 */}
        <section className="relative">
          <HeroBanner />
        </section>

        {/* Keyword Section (below hero) */}
        <section className="py-8 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed">
              Intense Infotech is a trusted website development company in India offering WordPress
              development, React web apps, SEO services, and custom business websites. We help
              startups and businesses grow online with fast, mobile-friendly, and SEO-optimized
              websites.
            </p>
          </div>
        </section>

        {/* Modern Services Section */}
        <ServicesSection />

        {/* Why Choose Us Section with H2 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Why Choose Intense Infotech for Web Development?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Fast & Responsive Development</h3>
                <p className="text-gray-600">We deliver high-performance web applications using React, Next.js, and modern frameworks for optimal user experience.</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">SEO-Optimized Solutions</h3>
                <p className="text-gray-600">Every website we build is optimized for search engines to help you rank higher on Google and drive organic traffic.</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">ROI-Focused Results</h3>
                <p className="text-gray-600">Our digital marketing strategies are designed to maximize your return on investment with measurable results.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <Stats />

        {/* Portfolio Section with H2 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Our Successful Web Development Projects
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              Explore our portfolio of successful web development projects across various industries including e-commerce, education, and enterprise solutions.
            </p>
            <Portfolio />
          </div>
        </section>

        {/* Clients Section with Auto-Slider */}
        <ClientSlider />

        {/* Blog Slider Section */}
        <BlogSlider />
        {/* Blog/News Section */}
        <BlogNews />

        {/* Contact Section with H2 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Ready to Start Your Web Development Project?
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              Get a free consultation and quote for your custom web development, React application, or digital marketing needs.
            </p>
            <ContactForm />
          </div>
        </section>

      
      </main>
    </>
  );
}
